import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import {
  useICPEmployeeCountDistribution,
  useICPGeographyDistribution,
  useICPIndustryDistribution,
  useICPRevenueDistribution,
  useICPWinLossEmployeeCountDistribution,
  useICPWinLossGeographyDistribution,
  useICPWinLossIndustryDistribution,
  useICPWinLossRevenueDistribution,
} from "../../../../hooks/useICPDistribution";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { ICPAnalysisMethod } from "../../types";
import { isAnalysisResultsSame } from "../../utils";
import { ICPChartsSection } from "../Charts/ICPChartsSection";
import { ICPEmployeeCountChart } from "../Charts/ICPEmployeeCountChart";
import { ICPEmployeeCountWinLossChart } from "../Charts/ICPEmployeeCountWinLossChart";
import { ICPGeographyChart } from "../Charts/ICPGeographyChart";
import { ICPGeographyWinLossChart } from "../Charts/ICPGeographyWinLossChart";
import { ICPIndustryChart } from "../Charts/ICPIndustryChart";
import { ICPRevenueChart } from "../Charts/ICPRevenueChart";
import { ICPRevenueWinLossChart } from "../Charts/ICPRevenueWinLossChart";
import { ICPWinLossChart } from "../Charts/ICPWinLossChart";

import { ICPSAMSOMSection } from "../SAMSOMSection/ICPSAMSOMSection";
import { ICPFirmographicsAnalysisSection } from "./ICPFirmographicsAnalysisSection";
import {
  getSignificantItems,
  getTopSignificantItem,
} from "./getSignificantItems";
import { useGlobalSettings } from "../../../../hooks/useGlobalSettings";

/**
 * ICP Analysis tab component
 */
export const ICPAnalysisTab: FC = () => {
  const globalSettings = useGlobalSettings();
  const icpSelectedProducts = useWorkspaceStore(
    (state) => state.icpSelectedProducts
  );
  const icpAnalysisMethod = useWorkspaceStore(
    (state) => state.icpAnalysisMethod
  );
  const icpCustomerListData = useWorkspaceStore(
    (state) => state.icpCustomerListData
  );
  const setIcpSAM = useWorkspaceStore((state) => state.setIcpSAM);
  const setIcpSOM = useWorkspaceStore((state) => state.setIcpSOM);
  const icpIndustryDistribution = useWorkspaceStore(
    (state) => state.icpIndustryDistribution
  );
  const setICPIndustryDistribution = useWorkspaceStore(
    (state) => state.setICPIndustryDistribution
  );
  const icpGeographyDistribution = useWorkspaceStore(
    (state) => state.icpGeographyDistribution
  );
  const setICPGeographyDistribution = useWorkspaceStore(
    (state) => state.setICPGeographyDistribution
  );
  const icpRevenueDistribution = useWorkspaceStore(
    (state) => state.icpRevenueDistribution
  );
  const setICPRevenueDistribution = useWorkspaceStore(
    (state) => state.setICPRevenueDistribution
  );
  const icpEmployeeCountDistribution = useWorkspaceStore(
    (state) => state.icpEmployeeCountDistribution
  );
  const setICPEmployeeCountDistribution = useWorkspaceStore(
    (state) => state.setICPEmployeeCountDistribution
  );

  const shouldSkipQuery =
    (icpAnalysisMethod === ICPAnalysisMethod.PRODUCT_BASED &&
      icpSelectedProducts.length === 0) ||
    (icpAnalysisMethod === ICPAnalysisMethod.CUSTOMER_LIST &&
      !icpCustomerListData?.companyMatch?.lastMatched);

  // Use individual hooks for individual chart loading
  const industryResult = useICPIndustryDistribution({
    productIds:
      icpAnalysisMethod === ICPAnalysisMethod.PRODUCT_BASED
        ? icpSelectedProducts
        : undefined,
    matchFile:
      icpAnalysisMethod === ICPAnalysisMethod.CUSTOMER_LIST
        ? icpCustomerListData?.companyMatch
        : undefined,
    globalSettings,
    skip: shouldSkipQuery,
  });

  const geographyResult = useICPGeographyDistribution({
    productIds:
      icpAnalysisMethod === ICPAnalysisMethod.PRODUCT_BASED
        ? icpSelectedProducts
        : undefined,
    matchFile:
      icpAnalysisMethod === ICPAnalysisMethod.CUSTOMER_LIST
        ? icpCustomerListData?.companyMatch
        : undefined,
    globalSettings,
    skip: shouldSkipQuery,
  });

  const revenueResult = useICPRevenueDistribution({
    productIds:
      icpAnalysisMethod === ICPAnalysisMethod.PRODUCT_BASED
        ? icpSelectedProducts
        : undefined,
    matchFile:
      icpAnalysisMethod === ICPAnalysisMethod.CUSTOMER_LIST
        ? icpCustomerListData?.companyMatch
        : undefined,
    globalSettings,
    skip: shouldSkipQuery,
  });

  const employeeCountResult = useICPEmployeeCountDistribution({
    productIds:
      icpAnalysisMethod === ICPAnalysisMethod.PRODUCT_BASED
        ? icpSelectedProducts
        : undefined,
    matchFile:
      icpAnalysisMethod === ICPAnalysisMethod.CUSTOMER_LIST
        ? icpCustomerListData?.companyMatch
        : undefined,
    globalSettings,
    skip: shouldSkipQuery,
  });

  // Extract data
  const { industryDistribution } = industryResult;
  const { geographyDistribution } = geographyResult;
  const { revenueDistribution } = revenueResult;
  const { employeeCountDistribution } = employeeCountResult;

  // Simple error handling - show error if any query fails
  const error =
    industryResult.error ||
    geographyResult.error ||
    revenueResult.error ||
    employeeCountResult.error;

  // Win/loss data is only available for customer list analysis
  const matchFile = icpCustomerListData?.companyMatch;
  const hasWinLossDataFromFile = useMemo(() => {
    if (!matchFile) return false;
    const salesWins = matchFile.salesWins || 0;
    const salesLosses = matchFile.salesLosses || 0;
    return Boolean(salesWins || salesLosses);
  }, [matchFile]);

  const shouldSkipWinLoss =
    !hasWinLossDataFromFile ||
    shouldSkipQuery ||
    icpAnalysisMethod !== ICPAnalysisMethod.CUSTOMER_LIST ||
    !matchFile?.lastMatched;

  const { winLossDistribution, loading: winLossIndustryLoading } =
    useICPWinLossIndustryDistribution({
      matchFile: matchFile!,
      globalSettings,
      skip: shouldSkipWinLoss,
    });

  const {
    winLossDistribution: geographyWinLossDistribution,
    loading: winLossGeographyLoading,
  } = useICPWinLossGeographyDistribution({
    matchFile: matchFile!,
    globalSettings,
    skip: shouldSkipWinLoss,
  });

  const {
    winLossDistribution: revenueWinLossDistribution,
    loading: winLossRevenueLoading,
  } = useICPWinLossRevenueDistribution({
    matchFile: matchFile!,
    globalSettings,
    skip: shouldSkipWinLoss,
  });

  const {
    winLossDistribution: employeeCountWinLossDistribution,
    loading: winLossEmployeeLoading,
  } = useICPWinLossEmployeeCountDistribution({
    matchFile: matchFile!,
    globalSettings,
    skip: shouldSkipWinLoss,
  });

  // Show page-level loader only when ALL distribution charts are loading (no data loaded yet)
  // Once any chart has data, show the page with individual chart skeletons
  const allDistributionChartsLoading =
    industryResult.loading &&
    geographyResult.loading &&
    revenueResult.loading &&
    employeeCountResult.loading;

  const hasAnyDistributionData =
    industryDistribution.length > 0 ||
    geographyDistribution.length > 0 ||
    revenueDistribution.length > 0 ||
    employeeCountDistribution.length > 0;

  // Show page loader only when all charts are loading AND we have no data yet
  const isOverallLoading =
    allDistributionChartsLoading && !hasAnyDistributionData;

  const areResultsSame = useMemo(
    () =>
      isAnalysisResultsSame({
        industryDistribution,
        icpIndustryDistribution,
        geographyDistribution,
        icpGeographyDistribution,
        revenueDistribution,
        icpRevenueDistribution,
        employeeCountDistribution,
        icpEmployeeCountDistribution,
      }),
    [
      industryDistribution,
      icpIndustryDistribution,
      geographyDistribution,
      icpGeographyDistribution,
      revenueDistribution,
      icpRevenueDistribution,
      employeeCountDistribution,
      icpEmployeeCountDistribution,
    ]
  );

  useEffect(() => {
    if (!isOverallLoading && !error && !shouldSkipQuery && !areResultsSame) {
      // Save the new distribution data to store
      setICPIndustryDistribution(industryDistribution);
      setICPGeographyDistribution(geographyDistribution);
      setICPRevenueDistribution(revenueDistribution);
      setICPEmployeeCountDistribution(employeeCountDistribution);

      const significantIndustries = getSignificantItems(
        industryDistribution
      ).map((item) => item.industryId);
      const significantCountries = getSignificantItems(
        geographyDistribution
      ).map((item) => item.countryCode);
      const topRevenueRange = getTopSignificantItem(revenueDistribution);
      const topEmployeeRange = getTopSignificantItem(employeeCountDistribution);

      setIcpSAM({
        industry: {
          ids: significantIndustries,
          sicIds: [],
          naicsIds: [],
          excludedIds: [],
          excludedSicIds: [],
          excludedNaicsIds: [],
        },
        geography: {
          countryIds: significantCountries,
          regionsIds: [],
          stateIds: [],
          excludedCountryIds: [],
        },
      });

      setIcpSOM({
        revenueRange: {
          min:
            topRevenueRange?.revenueRangeMin === -1 ||
            topRevenueRange?.revenueRangeMin === null
              ? undefined
              : (topRevenueRange?.revenueRangeMin ?? 0),
          max:
            topRevenueRange?.revenueRangeMax === -1 ||
            topRevenueRange?.revenueRangeMax === null
              ? undefined
              : (topRevenueRange?.revenueRangeMax ?? 0),
        },
        employeeRange: {
          min:
            topEmployeeRange?.employeesRangeMin === -1 ||
            topEmployeeRange?.employeesRangeMin === null
              ? undefined
              : (topEmployeeRange?.employeesRangeMin ?? 0),
          max:
            topEmployeeRange?.employeesRangeMax === -1 ||
            topEmployeeRange?.employeesRangeMax === null
              ? undefined
              : (topEmployeeRange?.employeesRangeMax ?? 0),
        },
        productIds: [],
        excludedProductIds: [],
        productsInclusionMethod: "ANY_PRESENT",
        excludedProductsInclusionMethod: "NONE_PRESENT",
      });
    }
  }, [
    isOverallLoading,
    error,
    shouldSkipQuery,
    industryDistribution,
    geographyDistribution,
    revenueDistribution,
    employeeCountDistribution,
    setIcpSAM,
    setIcpSOM,
    setICPIndustryDistribution,
    setICPGeographyDistribution,
    setICPRevenueDistribution,
    setICPEmployeeCountDistribution,
    areResultsSame,
  ]);

  if (shouldSkipQuery) {
    return (
      <Box sx={{ padding: "30px" }}>
        <Typography
          variant="h4"
          textTransform="capitalize"
          sx={{ marginBottom: 2 }}
        >
          Analysis Results
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please select products or upload a customer list in the Definition tab
          to view analysis results.
        </Typography>
      </Box>
    );
  }

  if (isOverallLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        padding="30px"
        gap={3}
      >
        <CircularProgress />
        <Stack alignItems="center">
          <Typography variant="body1" marginLeft={2}>
            Processing your data and generating insights...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This may take a few minutes.
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: "30px" }}>
        <Typography
          variant="h4"
          textTransform="capitalize"
          sx={{ marginBottom: 2 }}
        >
          Analysis Results
        </Typography>
        <Typography variant="body1" color="error">
          Error loading analysis: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: "30px",
        gap: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="h4"
        textTransform="capitalize"
        sx={{ marginBottom: 2 }}
      >
        Analysis Results
      </Typography>
      <ICPSAMSOMSection />

      <ICPFirmographicsAnalysisSection>
        <Stack gap={3}>
          <ICPChartsSection title="Industry">
            {/* Win/Loss chart - only show for customer list analysis with win/loss data */}
            {!shouldSkipWinLoss && (
              <ICPWinLossChart
                data={winLossDistribution}
                totalCount={industryDistribution.reduce(
                  (sum, item) => sum + item.count,
                  0
                )}
                distributionData={industryDistribution}
                isLoading={winLossIndustryLoading}
              />
            )}
            <ICPIndustryChart
              data={industryDistribution}
              isLoading={industryResult.loading}
            />
          </ICPChartsSection>
          <ICPChartsSection title="Geography">
            {/* Win/Loss chart - only show for customer list analysis with win/loss data */}
            {!shouldSkipWinLoss && (
              <ICPGeographyWinLossChart
                data={geographyWinLossDistribution}
                totalCount={geographyDistribution.reduce(
                  (sum, item) => sum + item.count,
                  0
                )}
                distributionData={geographyDistribution}
                isLoading={winLossGeographyLoading}
              />
            )}
            <ICPGeographyChart
              data={geographyDistribution}
              isLoading={geographyResult.loading}
            />
          </ICPChartsSection>
          <Box display="flex" width="100%" gap={3}>
            <ICPChartsSection title="Revenue">
              {/* Win/Loss chart - only show for customer list analysis with win/loss data */}
              {!shouldSkipWinLoss && (
                <ICPRevenueWinLossChart
                  data={revenueWinLossDistribution}
                  totalCount={revenueDistribution.reduce(
                    (sum, item) => sum + item.count,
                    0
                  )}
                  isLoading={winLossRevenueLoading}
                />
              )}
              <ICPRevenueChart
                data={revenueDistribution}
                isLoading={revenueResult.loading}
              />
            </ICPChartsSection>

            <ICPChartsSection title="Employees">
              {/* Win/Loss chart - only show for customer list analysis with win/loss data */}
              {!shouldSkipWinLoss && (
                <ICPEmployeeCountWinLossChart
                  data={employeeCountWinLossDistribution}
                  totalCount={employeeCountDistribution.reduce(
                    (sum, item) => sum + item.count,
                    0
                  )}
                  isLoading={winLossEmployeeLoading}
                />
              )}
              <ICPEmployeeCountChart
                data={employeeCountDistribution}
                isLoading={employeeCountResult.loading}
              />
            </ICPChartsSection>
          </Box>
        </Stack>
      </ICPFirmographicsAnalysisSection>
    </Box>
  );
};
